import { types } from "../types/types";
import { 
  URL_GET_SOLICITUDES_POR_USUARIO,
  URL_NUEVA_SOLICITUD,
  URL_LISTA_COMENTARIOS,
  URL_NUEVO_COMENTARIO,
  URL_CERRAR_SOLICITUD,
  URL_CONFIRMAR_LECTURA,
  URL_CARGAR_DOCUMENTO_SOLICITUD,
  URL_LISTA_ARCHIVOS_POR_SOLICITUD,
  URL_GET_FILE,
  URL_BORRAR_DOCUMENTO_SOLICITUD
} from "../../constants/url";
import { get, post, postFile } from "../../utils/fetch";
import { notification } from "./notificationActions";
import {
  composeErrorNotification,
  composeSuccessNotification,
} from "../../utils/composeNotification";

export const getSolicitudes = () => {
  return async (dispatch) => {
    dispatch(setLoadingSolicitudes(true));
    
    try {
      const response = await get(URL_GET_SOLICITUDES_POR_USUARIO);
      
      
      // La API devuelve directamente el array de solicitudes
      if (Array.isArray(response)) {
        dispatch(storeSolicitudes(response));
        
        // Calcular mensajes no leídos basándose en los datos reales de la API
        const totalNoLeidos = response.reduce((acc, sol) => 
          acc + (sol.ultimoComentarioHistorial && !sol.ultimoComentarioHistorial.leido ? 1 : 0), 0);
        dispatch(setMensajesNoLeidos(totalNoLeidos));
        
        // Obtener últimos mensajes para cada solicitud
        response.forEach(solicitud => {
          dispatch(getUltimoMensajeSolicitud(solicitud.proveedorSolicitudID));
        });
      } else if (response.code !== -1) {
        dispatch(storeSolicitudes(response.data));
        
        // Obtener últimos mensajes para cada solicitud
        if (response.data && Array.isArray(response.data)) {
          response.data.forEach(solicitud => {
            dispatch(getUltimoMensajeSolicitud(solicitud.proveedorSolicitudID));
          });
        }
      } else {
        dispatch(notification(composeErrorNotification("GetSolicitudes", response.error || "Error desconocido")));
      }
    } catch (error) {
      dispatch(notification(composeErrorNotification("GetSolicitudes", error.message)));
    }
    
    dispatch(setLoadingSolicitudes(false));
  };
};

export const createSolicitud = (solicitudData) => {
  return async (dispatch) => {
    dispatch(setCreandoSolicitud(true));
    
    // Update optimista: agregar solicitud temporal a la lista
    const tempSolicitud = {
      proveedorSolicitudID: Date.now(), // ID temporal
      comentario: solicitudData.descripcion || solicitudData.comentario,
      fechaCreacion: new Date().toISOString(),
      estatus: "Abierto",
      mensajesNoLeidos: 0,
      proveedorUsuarioNombre: "Usuario Actual",
      isTemporary: true
    };
    dispatch(addSolicitudOptimista(tempSolicitud));
    
    try {
      const payload = { comentario: solicitudData.descripcion || solicitudData.comentario };
      const response = await post(URL_NUEVA_SOLICITUD, payload);
      
      if ((typeof response === 'string' && response.includes('correctamente')) ||
          (response.code === 0 && response.data && response.data.includes('correctamente')) ||
          (response.code !== -1)) {
        dispatch(notification(composeSuccessNotification("Solicitud creada exitosamente")));
        // Obtener solicitudes actualizadas silenciosamente
        dispatch(getSolicitudesSilencioso());
        return { success: true };
      } else {
        // Revertir update optimista
        dispatch(removeSolicitudOptimista(tempSolicitud.proveedorSolicitudID));
        dispatch(notification(composeErrorNotification("CreateSolicitud", response.error || "Error al crear solicitud")));
        return { success: false, error: response.error || "Error al crear solicitud" };
      }
    } catch (error) {
      // Revertir update optimista
      dispatch(removeSolicitudOptimista(tempSolicitud.proveedorSolicitudID));
      dispatch(notification(composeErrorNotification("CreateSolicitud", error.message)));
      return { success: false, error: error.message };
    } finally {
      dispatch(setCreandoSolicitud(false));
    }
  };
};

export const getHistorialSolicitud = (solicitudId) => {
  return async (dispatch) => {
    dispatch(setLoadingSolicitudes(true));
    
    try {
      const response = await get(`${URL_LISTA_COMENTARIOS}/${solicitudId}`);
      
      // La API devuelve directamente el array de comentarios
      if (Array.isArray(response)) {
        dispatch(storeHistorialSolicitud(response));
        dispatch(setSolicitudActiva(solicitudId));
      } else if (response.code !== -1) {
        dispatch(storeHistorialSolicitud(response.data));
        dispatch(setSolicitudActiva(solicitudId));
      } else {
        dispatch(notification(composeErrorNotification("GetHistorial", response.error || "Error al obtener historial")));
      }
    } catch (error) {
      dispatch(notification(composeErrorNotification("GetHistorial", error.message)));
    }
    
    dispatch(setLoadingSolicitudes(false));
  };
};

export const addMensaje = (mensajeData) => {
  return async (dispatch) => {
    const payload = {
      solicitudID: mensajeData.solicitudID || mensajeData.solicitudId,
      comentario: mensajeData.comentario || mensajeData.mensaje
    };
    
    // Update optimista: agregar mensaje temporal al historial
    const tempMensaje = {
      proveedorSolicitudHistorialID: Date.now(),
      comentario: payload.comentario,
      fechaCreacion: new Date().toISOString(),
      usuarioNombre: "Tú",
      leido: true,
      isTemporary: true
    };
    dispatch(addMensajeOptimista(tempMensaje));
    
    try {
      const response = await post(URL_NUEVO_COMENTARIO, payload);
      
      if ((typeof response === 'string' && response.includes('Guardado')) || 
          (response.code === 0 && response.data && response.data.includes('Guardado'))) {
        dispatch(notification(composeSuccessNotification("Mensaje enviado")));
        // Actualizar historial silenciosamente
        dispatch(getHistorialSolicitudSilencioso(payload.solicitudID));
      } else if (response.code !== -1) {
        dispatch(notification(composeSuccessNotification("Mensaje enviado")));
        dispatch(getHistorialSolicitudSilencioso(payload.solicitudID));
      } else {
        // Revertir mensaje optimista
        dispatch(removeMensajeOptimista(tempMensaje.proveedorSolicitudHistorialID));
        dispatch(notification(composeErrorNotification("AddMensaje", response.error || "Error al enviar mensaje")));
      }
    } catch (error) {
      // Revertir mensaje optimista
      dispatch(removeMensajeOptimista(tempMensaje.proveedorSolicitudHistorialID));
      dispatch(notification(composeErrorNotification("AddMensaje", error.message)));
    }
  };
};

export const cerrarSolicitud = (solicitudId) => {
  return async (dispatch) => {
    // Update optimista: marcar solicitud como cerrada
    dispatch(updateSolicitudEstatus(solicitudId, "Cerrado"));
    
    try {
      const payload = { solicitudID: solicitudId };
      const response = await post(URL_CERRAR_SOLICITUD, payload);
      
      if ((typeof response === 'string' && response.includes('correctamente')) ||
          (response.code === 0 && response.data && response.data.includes('correctamente')) ||
          (response.code !== -1)) {
        dispatch(notification(composeSuccessNotification("Solicitud cerrada exitosamente")));
        // Actualizar lista silenciosamente para confirmar cambios
        dispatch(getSolicitudesSilencioso());
      } else {
        // Revertir cambio optimista
        dispatch(updateSolicitudEstatus(solicitudId, "Abierto"));
        dispatch(notification(composeErrorNotification("CerrarSolicitud", "Error al cerrar solicitud")));
      }
    } catch (error) {
      // Revertir cambio optimista
      dispatch(updateSolicitudEstatus(solicitudId, "Abierto"));
      dispatch(notification(composeErrorNotification("CerrarSolicitud", error.message)));
    }
  };
};

export const confirmarLecturaComentario = (solicitudId) => {
  return async (dispatch) => {
    try {
      const payload = { solicitudID: solicitudId };
      await post(URL_CONFIRMAR_LECTURA, payload);
      
      dispatch(getSolicitudesSilencioso());
    } catch (error) {
    }
  };
};

const storeSolicitudes = (payload) => ({
  type: types.storeSolicitudes,
  payload
});

const storeHistorialSolicitud = (payload) => ({
  type: types.storeHistorialSolicitud,
  payload
});

const setLoadingSolicitudes = (payload) => ({
  type: types.setLoadingSolicitudes,
  payload
});

const setCreandoSolicitud = (payload) => ({
  type: types.setCreandoSolicitud,
  payload
});

const addMensajeSolicitud = (payload) => ({
  type: types.addMensajeSolicitud,
  payload
});

const setSolicitudActiva = (payload) => ({
  type: types.setSolicitudActiva,
  payload
});

const setMensajesNoLeidos = (payload) => ({
  type: types.setMensajesNoLeidos,
  payload
});

// Funciones silenciosas sin loading indicators
export const getSolicitudesSilencioso = () => {
  return async (dispatch) => {
    try {
      const response = await get(URL_GET_SOLICITUDES_POR_USUARIO);
      
      if (Array.isArray(response)) {
        dispatch(storeSolicitudes(response));
        const totalNoLeidos = response.reduce((acc, sol) => 
          acc + (sol.ultimoComentarioHistorial && !sol.ultimoComentarioHistorial.leido ? 1 : 0), 0);
        dispatch(setMensajesNoLeidos(totalNoLeidos));
        
        // obtener últimos mensajes para cada solicitud
        response.forEach(solicitud => {
          dispatch(getUltimoMensajeSolicitud(solicitud.proveedorSolicitudID));
        });
      } else if (response.code !== -1) {
        dispatch(storeSolicitudes(response.data));
        
        // obtener últimos mensajes para cada solicitud
        if (response.data && Array.isArray(response.data)) {
          response.data.forEach(solicitud => {
            dispatch(getUltimoMensajeSolicitud(solicitud.proveedorSolicitudID));
          });
        }
      }
    } catch (error) {
      // Error silencioso
    }
  };
};

export const getHistorialSolicitudSilencioso = (solicitudId) => {
  return async (dispatch) => {
    try {
      const response = await get(`${URL_LISTA_COMENTARIOS}/${solicitudId}`);
      
      if (Array.isArray(response)) {
        dispatch(storeHistorialSolicitud(response));
      } else if (response.code !== -1) {
        dispatch(storeHistorialSolicitud(response.data));
      }
    } catch (error) {
    }
  };
};

// Función para obtener solo el último mensaje de una solicitud (sin loading)
export const getUltimoMensajeSolicitud = (solicitudId) => {
  return async (dispatch) => {
    try {
      const response = await get(`${URL_LISTA_COMENTARIOS}/${solicitudId}`);
      
      if (Array.isArray(response) && response.length > 0) {
        // Obtener el último mensaje real (no temporal)
        const mensajesReales = response.filter(msg => !msg.isTemporary);
        if (mensajesReales.length > 0) {
          const ultimoMensaje = mensajesReales[mensajesReales.length - 1];
          dispatch(storeUltimoMensaje({
            solicitudId,
            ultimoMensaje: {
              comentario: ultimoMensaje.comentario,
              fechaCreacion: ultimoMensaje.fechaCreacion,
              usuarioNombre: ultimoMensaje.usuarioNombre
            }
          }));
        }
      } else if (response.code !== -1 && response.data && response.data.length > 0) {
        const mensajesReales = response.data.filter(msg => !msg.isTemporary);
        if (mensajesReales.length > 0) {
          const ultimoMensaje = mensajesReales[mensajesReales.length - 1];
          dispatch(storeUltimoMensaje({
            solicitudId,
            ultimoMensaje: {
              comentario: ultimoMensaje.comentario,
              fechaCreacion: ultimoMensaje.fechaCreacion,
              usuarioNombre: ultimoMensaje.usuarioNombre
            }
          }));
        }
      }
    } catch (error) {
    }
  };
};

// Actions para updates optimistas
const addSolicitudOptimista = (payload) => ({
  type: types.addSolicitudOptimista,
  payload
});

const removeSolicitudOptimista = (payload) => ({
  type: types.removeSolicitudOptimista,
  payload
});

const addMensajeOptimista = (payload) => ({
  type: types.addMensajeOptimista,
  payload
});

const removeMensajeOptimista = (payload) => ({
  type: types.removeMensajeOptimista,
  payload
});

const updateSolicitudEstatus = (solicitudId, estatus) => ({
  type: types.updateSolicitudEstatus,
  payload: { solicitudId, estatus }
});

const storeUltimoMensaje = (payload) => ({
  type: types.storeUltimoMensaje,
  payload
});

// ACCIONES PARA ARCHIVOS

// Obtener archivos de una solicitud
export const getArchivosSolicitud = (solicitudId) => {
  return async (dispatch, getState) => {
    dispatch(setLoadingArchivos(true));
    
    try {
      const response = await get(`${URL_LISTA_ARCHIVOS_POR_SOLICITUD}/${solicitudId}`);
      const { solicitudes } = getState().solicitudesReducer;
      
      let archivos = [];
      if (response.code === 0 && Array.isArray(response.data)) {
        archivos = response.data;
      } else if (Array.isArray(response)) {
        archivos = response;
      }
      
      // Enriquecer archivos con info de usuario
      const archivosEnriquecidos = archivos.map(archivo => {
        const solicitud = solicitudes.find(s => s.proveedorSolicitudID == archivo.proveedorSolicitudID);
        return {
          ...archivo,
          usuarioNombre: solicitud?.proveedorUsuarioNombre || "Usuario desconocido",
          usuarioID: solicitud?.proveedorUsuarioID
        };
      });
      
      dispatch(storeArchivosSolicitud(archivosEnriquecidos));
    } catch (error) {
      dispatch(notification(composeErrorNotification("GetArchivos", error.message)));
    }
    
    dispatch(setLoadingArchivos(false));
  };
};

// Subir archivo
export const subirArchivo = (solicitudId, file) => {
  return async (dispatch) => {
    dispatch(setSubiendoArchivo(true));
    
    try {
      const formData = new FormData();
      formData.append('proveedorSolicitudID', solicitudId);
      formData.append('file', file);
      
      const response = await postFile(URL_CARGAR_DOCUMENTO_SOLICITUD, formData);
      
      if (response.code === 0 || (typeof response.data === 'string' && response.data.includes('Guardado'))) {
        dispatch(notification(composeSuccessNotification("Archivo subido exitosamente")));
        // Recargar archivos
        dispatch(getArchivosSolicitud(solicitudId));
      } else {
        dispatch(notification(composeErrorNotification("SubirArchivo", response.error || "Error al subir archivo")));
      }
    } catch (error) {
      dispatch(notification(composeErrorNotification("SubirArchivo", error.message)));
    }
    
    dispatch(setSubiendoArchivo(false));
  };
};

// Eliminar archivo
export const eliminarArchivo = (documentoId, solicitudId) => {
  return async (dispatch) => {
    try {
      const response = await get(`${URL_BORRAR_DOCUMENTO_SOLICITUD}/${documentoId}`);
      
      if (response.code === 0 || 
          (typeof response.data === 'string' && response.data.includes('eliminado')) ||
          (typeof response === 'string' && response.includes('eliminado'))) {
        dispatch(notification(composeSuccessNotification("Archivo eliminado")));
        // Recargar archivos
        dispatch(getArchivosSolicitud(solicitudId));
      } else {
        dispatch(notification(composeErrorNotification("EliminarArchivo", response.error || "Error al eliminar archivo")));
      }
    } catch (error) {
      dispatch(notification(composeErrorNotification("EliminarArchivo", error.message)));
    }
  };
};

// Descargar archivo
export const descargarArchivo = (documentoId, nombreFile) => {
  return async (dispatch) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${URL_GET_FILE}/${documentoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreFile;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        dispatch(notification(composeSuccessNotification("Archivo descargado")));
      } else {
        dispatch(notification(composeErrorNotification("DescargarArchivo", "Error al descargar archivo")));
      }
    } catch (error) {
      dispatch(notification(composeErrorNotification("DescargarArchivo", error.message)));
    }
  };
};

// recarga completa silenciosa
export const reloadAllSilencioso = (solicitudIdActiva = null) => {
  return async (dispatch) => {
    // 1. recargar lista principal de solicitudes (incluye últimos mensajes)
    dispatch(getSolicitudesSilencioso());
    
    // 2. si hay una solicitud activa, recargar su historial y archivos
    if (solicitudIdActiva) {
      dispatch(getHistorialSolicitudSilencioso(solicitudIdActiva));
      dispatch(getArchivosSolicitud(solicitudIdActiva));
    }
  };
};

// Action creators para archivos
const storeArchivosSolicitud = (payload) => ({
  type: types.storeArchivosSolicitud,
  payload
});

const setLoadingArchivos = (payload) => ({
  type: types.setLoadingArchivos,
  payload
});

const setSubiendoArchivo = (payload) => ({
  type: types.setSubiendoArchivo,
  payload
});